import React from "react";
import { config } from "../../config/config";
import { signup, login } from "../../services/authenticationService";
import {
  HeadingStyled,
  ContainerStyled,
  ButtonsGroupStyled,
  KeyButtonStyledBack,
  KeyButtonStyledNext,
  TextStyled
} from "../../styles/shared-styles";
import { SignupPageStyled, BlockSignupStyled } from "./styles/Styled";
import { Text, Input, KeyboardSwap, withFocusable, Loading } from "../../services/visualLibraryService";
import { withAppState, getAppState } from "../../state";
import { i18n } from "../../services/i18nService";
import { getSdk } from "../../services/sdkService";
import { withSubtheme } from "../../styles/themes/withSubtheme";
import { getGlobalButtonFocusPath, getGlobalButtonInfo } from "../../errors/globalButtons";
import {
  getInternetConnectivityModalConfig,
  getLoginErrorModalConfig,
  getSignupInvalidEmailError
} from "../../errors/utils";
import { trackPage, trackEvent } from "../../services/analyticsService";
import { isValidEmail } from "../../utils/isEmail";
import { showExitSignupModal, showSingleModalButton } from "../../services/modalsService";
import { historyMethods } from "../../utils/historyMethods";
import { redirectTo } from "../../services/navigationService";
import { routes } from "../../routes";

const PAGE = "createAccount";
const SIGNUP = "signup";
const KeyButtonStyledBackFocus = withFocusable(KeyButtonStyledBack);
const KeyButtonStyledNextFocus = withFocusable(KeyButtonStyledNext);
const KeyButtonStyledShowHideFocus = withFocusable(KeyButtonStyledNext);
const TYPE_PASSWORD = "password";
const TYPE_TEXT = "text";
const BLACK_LISTED_EMAIL_ERROR = "10016";
const EMAIL_ALREADY_EXIST_ERROR = ["900001", "10004"];
const LOGIN_BUTTON_LABEL = "login";
const BACK_BUTTON_LABEL = "back";
const CLOSE_BUTTON_LABEL = "close";
const CANCEL_BUTTON_LABEL = "cancel";

function getSignupErrorModalConfig(errorCode) {
  let modalTitle, modalText, buttons;

  if (EMAIL_ALREADY_EXIST_ERROR.includes(errorCode)) {
    modalTitle = i18n.get("pages.signup.errors.alreadyExist.title");
    modalText = i18n.get("pages.signup.errors.alreadyExist.text");
    buttons = [
      {
        ...getGlobalButtonInfo(LOGIN_BUTTON_LABEL),
        buttonHandler: redirectTo.bind(
          this,
          routes.login,
          { nextPage: routes.redeem, user: this.state.user },
          historyMethods.REPLACE
        )
      },
      {
        ...getGlobalButtonInfo(BACK_BUTTON_LABEL),
        buttonHandler: this.closeModal
      }
    ];
  } else {
    modalText = i18n.get("pages.signup.errors.generic");
  }
  return {
    modalTitle,
    modalText,
    buttons: buttons || showSingleModalButton(BACK_BUTTON_LABEL, this.closeModal)
  };
}

function handleRequestError(error) {
  const errorCode = error.error_code;
  let buttonPath = EMAIL_ALREADY_EXIST_ERROR.includes(errorCode) ? LOGIN_BUTTON_LABEL : BACK_BUTTON_LABEL;
  const { stateActions, setFocus } = this.props;
  trackEvent(SIGNUP, "account_creation_error", error.message);
  if (error.status === 400) {
    if (errorCode === BLACK_LISTED_EMAIL_ERROR) {
      buttonPath = CLOSE_BUTTON_LABEL;
      stateActions.showGlobalModal({
        modalText: i18n.get("pages.signup.errors.emailBlackListed"),
        buttons: showSingleModalButton(CLOSE_BUTTON_LABEL, this.closeModal)
      });
    } else {
      stateActions.showGlobalModal(getSignupErrorModalConfig.call(this, errorCode));
    }
  } else if (error.status === 422) {
    stateActions.showGlobalModal({
      modalText: i18n.get("pages.signup.errors.invalidEmail"),
      buttons: showSingleModalButton(BACK_BUTTON_LABEL, this.closeModal)
    });
  } else if (getAppState().isOnline && error.message.indexOf("timeout") > -1) {
    stateActions.showGlobalModal(
      getInternetConnectivityModalConfig({
        retryImplementation: this.closeModal,
        backImplementation: this.closeModal,
        customButtons: showSingleModalButton(BACK_BUTTON_LABEL, this.closeModal),
        needsDismissableProp: false,
        isTimeout: true
      })
    );
  } else if (!getAppState().isOnline) {
    stateActions.showGlobalModal(
      getInternetConnectivityModalConfig({
        retryImplementation: this.closeModal,
        backImplementation: this.closeModal,
        customButtons: showSingleModalButton(CLOSE_BUTTON_LABEL, this.closeModal),
        needsDismissableProp: false
      })
    );
  } else {
    stateActions.showGlobalModal({
      modalText: i18n.get("pages.signup.errors.generic"),
      buttons: showSingleModalButton(BACK_BUTTON_LABEL, this.closeModal)
    });
  }
  setFocus(getGlobalButtonFocusPath(buttonPath));
}

async function storeUserData(responseData) {
  const { stateActions } = this.props;
  stateActions.userLoggedIn(responseData);
  const sdk = await getSdk();
  sdk.userManager.getDevices();
  sdk.userManager.getPreferences();
}

async function handleSignup(signupData, autologin) {
  try {
    const {
      stateActions: { showGlobalModal },
      setFocus
    } = this.props;
    const signupResponse = await signup(signupData, autologin);
    if (signupResponse && signupResponse.error_code && signupResponse.error_code === 207) {
      trackEvent(SIGNUP, "account_autologin_error", signupResponse.message);
      showGlobalModal(
        getLoginErrorModalConfig.call(this, {
          isRetrying: true,
          retryImplementation: this.login,
          cancelImplementation: redirectTo.bind(this, routes.home, null, historyMethods.REPLACE),
          isFromSignup: true
        })
      );
      setFocus(getGlobalButtonFocusPath(CANCEL_BUTTON_LABEL));
    } else {
      trackEvent(SIGNUP, "account_creation_success");
      if (!!autologin) {
        await storeUserData.call(this, signupResponse);
        trackEvent(SIGNUP, "account_autologin_success");
        redirectTo.call(this, routes.redeem, { fromSignup: true }, historyMethods.REPLACE);
      }
    }
  } catch (e) {
    this.setState({ isSigningUp: false });
    handleRequestError.call(this, e);
  }
}

class Signup extends React.Component {
  constructor(props) {
    super(props);
    this.userInput = React.createRef();
    this.pwdInput = React.createRef();
    this.state = {
      user: "",
      pwd: "",
      active: this.userInput,
      passInputType: TYPE_PASSWORD,
      isSigningUp: false
    };
    this.sendSignup = this.sendSignup.bind(this);
    this.login = this.login.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.activateUserInput = this.activateUserInput.bind(this);
    this.activatePasswordInputOrSignup = this.activatePasswordInputOrSignup.bind(this);
    this.checkSignupButtonDisability = this.checkSignupButtonDisability.bind(this);
    this.changePassInputType = this.changePassInputType.bind(this);
    this.keyboardDidSwap = this.keyboardDidSwap.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.onBack = this.onBack.bind(this);
  }

  componentDidMount() {
    trackPage(PAGE);
    this.props.onRef && this.props.onRef(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.currentFocus !== this.props.currentFocus ||
      nextState.user !== this.state.user ||
      nextState.pwd !== this.state.pwd ||
      nextState.active.current !== this.state.active.current ||
      nextState.passInputType !== this.state.passInputType ||
      nextState.isSigningUp !== this.state.isSigningUp
    );
  }

  async sendSignup() {
    const { user, pwd } = this.state;
    this.setState({ isSigningUp: true });
    const autologin = true;
    const signupData = { user, password: pwd };
    // NOTE: This condition has been included to prevent users from sending a number and not a valid email
    // address as the user input, since OTP validation for phone numbers is not implemented in our middlewares side
    // for the time being. Once it is, it is likely that we get rid of this condition.
    if (isValidEmail(user)) {
      handleSignup.call(this, signupData, autologin);
    } else {
      getSignupInvalidEmailError(user);
    }
  }

  async login() {
    const { user, pwd } = this.state;
    try {
      this.setState({ isSigningUp: true });
      const userLoggedRequest = await login(user, pwd, !getAppState().isOnline);
      this.setState({ isSigningUp: false });
      await storeUserData.call(this, userLoggedRequest);
      trackEvent(SIGNUP, "account_autologin_success");
      redirectTo.call(this, routes.redeem, { fromSignup: true }, historyMethods.REPLACE);
    } catch (e) {
      this.setState({ isSigningUp: false });
      handleRequestError.call(this, e);
    }
  }

  closeModal() {
    const {
      stateActions: { hideGlobalModal },
      setFocus
    } = this.props;
    const currentDirection = i18n.getDirection().toUpperCase();
    const initialDefaultFocus = KeyboardSwap[`defaultFocusPath${currentDirection}`];
    hideGlobalModal();
    setFocus(initialDefaultFocus);
  }

  async activateUserInput() {
    if (this.state.active === this.userInput) {
      showExitSignupModal.call(this);
    } else {
      await this.setState({
        active: this.userInput
      });
    }
  }

  activatePasswordInputOrSignup() {
    if (
      this.state.user.length &&
      this.state.pwd.length >= config.get().minPassLength &&
      !!this.state.pwd.length &&
      this.state.active === this.pwdInput
    ) {
      this.sendSignup();
    } else {
      const currentDirection = i18n.getDirection().toUpperCase();
      const initialDefaultFocus = KeyboardSwap[`defaultFocusPath${currentDirection}`];
      this.setState({
        active: this.pwdInput
      });
      this.props.setFocus(initialDefaultFocus);
    }
  }

  updateValue(value) {
    if (this.state.active === this.userInput && this.state.user !== value) {
      this.setState({ user: value });
    } else if (this.state.active === this.pwdInput && this.state.pwd !== value) {
      this.setState({ pwd: value });
    }
  }

  checkSignupButtonDisability() {
    return (
      (this.state.active === this.userInput && !this.state.user.length) ||
      (this.state.active === this.pwdInput && this.state.pwd.length < config.get().minPassLength)
    );
  }

  changePassInputType() {
    this.setState({
      passInputType: this.state.passInputType === TYPE_TEXT ? TYPE_PASSWORD : TYPE_TEXT
    });
  }

  onBack() {
    const globalModal = getAppState().globalModal;
    if (globalModal && globalModal.dismissable && !getAppState().isOnline) {
      this.props.stateActions.hideGlobalModal();
    } else if (globalModal && globalModal.dismissHandler) {
      globalModal.dismissHandler();
    } else if (this.state.active === this.userInput || !globalModal || globalModal.dismissable) {
      showExitSignupModal.call(this);
    }
    return true;
  }

  keyboardDidSwap({ defaultFocusPath }) {
    this.props.setFocus(defaultFocusPath);
  }

  render() {
    return (
      <>
        <SignupPageStyled>
          <HeadingStyled rank={1} text={i18n.get("pages.signup.title")} />
          <Text text={i18n.get("global.credentials.enterCredentials")} />
          <ContainerStyled>
            <BlockSignupStyled>
              <KeyboardSwap
                text={this.state.active === this.userInput ? this.state.user : this.state.pwd}
                onChange={this.updateValue}
                direction={i18n.getDirection()}
                onSwap={this.keyboardDidSwap}
              />
              <ButtonsGroupStyled>
                <KeyButtonStyledBackFocus onEnterPress={this.activateUserInput} focusPath={"signupBackButton"}>
                  {i18n.get("global.back")}
                </KeyButtonStyledBackFocus>

                <KeyButtonStyledNextFocus
                  onEnterPress={this.activatePasswordInputOrSignup}
                  focusPath={"signupNextButton"}
                  disabled={this.checkSignupButtonDisability()}
                >
                  {this.state.active === this.pwdInput ? i18n.get("pages.signup.title") : i18n.get("global.next")}
                </KeyButtonStyledNextFocus>
              </ButtonsGroupStyled>

              {this.state.active === this.pwdInput && (
                <KeyButtonStyledShowHideFocus
                  onClick={this.changePassInputType}
                  focusPath={"showHidePassword"}
                  icon={this.state.passInputType === TYPE_TEXT ? "hide" : "show"}
                >
                  {this.state.passInputType === TYPE_TEXT
                    ? i18n.get("global.credentials.hidePassword")
                    : i18n.get("global.credentials.showPassword")}
                </KeyButtonStyledShowHideFocus>
              )}
            </BlockSignupStyled>
            <BlockSignupStyled>
              <Input
                id={"signup-user-input"}
                type="text"
                ref={this.userInput}
                value={this.state.user}
                label={i18n.get("global.credentials.example.emailOnly")}
                active={this.state.active === this.userInput}
                readOnly
              />
              <Input
                id={"signup-password-input"}
                type={this.state.passInputType}
                ref={this.pwdInput}
                value={this.state.pwd}
                label={i18n.get("global.credentials.password")}
                active={this.state.active === this.pwdInput}
                readOnly
              />
            </BlockSignupStyled>
          </ContainerStyled>
          <TextStyled type="secondary" text={i18n.get("pages.signup.disclaimer")} />

          {this.state.isSigningUp && <Loading overlay />}
        </SignupPageStyled>
      </>
    );
  }
}

const Component = withAppState(
  withSubtheme(props => <Signup currentFocus={props.applicationState.currentFocus} {...props} />)
);

Component.defaultFocusPathLTR = KeyboardSwap.defaultFocusPathLTR;
Component.defaultFocusPathRTL = KeyboardSwap.defaultFocusPathRTL;
Component.defaultFocusPath = KeyboardSwap.defaultFocusPath;

export default Component;
